import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
  formatDateTime,
  numberFormat,
} from "../../utils/helpers";
import { URL_PENALTY_SETTINGS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_DISTRICTS, API_PENALTY_SETTINGS } from "../../utils/apis";

export const usePenaltySettings = () => {
  const tableColumns = () => [
    createColumn("district", true, "District"),
    createColumn("penalty_amount", true, "Penalty Amount"),
    createColumn("surcharge", true, "Surcharge Amount"),
    createColumn("grace_period", true, "Grace Period"),
    createColumn("created_at", false, "Created At"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        district: item?.district_name,
        penalty_amount: `${item?.penalty_amount ? numberFormat(item.penalty_amount) : "N/A"}`,
        surcharge: `${item?.surcharge ? numberFormat(item.surcharge) : "N/A"}`,
        created_at: item?.created_at ? formatDateTime(item.created_at) : "N/A",
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_PENALTY_SETTINGS, item?.id),
              createEditAction(URL_PENALTY_SETTINGS, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Penalty Setting Details",
          fields: {
            district: item?.district ? item.district : "N/A",
            penalty_amount: item?.penalty_amount
              ? `UGX ${parseFloat(item.penalty_amount).toLocaleString()}`
              : "N/A",
            surcharge: item?.surcharge
              ? `UGX ${parseFloat(item.surcharge).toLocaleString()}`
              : "N/A",
            grace_period: item?.grace_period ? item.grace_period : "N/A",
            status: item?.deleted ? "Deleted" : "Active",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`.trim() ||
                "System"
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`.trim() ||
                "Not updated"
              : "Not updated",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_PENALTY_SETTINGS);
  const activityFilter = "penalty_settings";

  return {
    tableData,
    tableColumns,
    viewData,
    formRoute,
    activityFilter,
  };
};

export const usePenaltySettingForm = ({
  // id,
  // form,
  // setError,
  updateFormValues,
}: TFormHookParams = {}) => {
  // Get existing penalty settings data
  const { loadData, loading, data } = useGet();
  const {
    loadData: loadDistricts,
    loading: loadingDistricts,
    data: districts,
  } = useGet();

  useEffect(() => {
    loadData({ api: API_PENALTY_SETTINGS });
    loadDistricts({ api: API_DISTRICTS });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues();
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Penalty Setting Details",
      fields: [
        {
          field_name: "district",
          label: "District",
          type: "select",
          props: {
            required: true,
            placeholder: "Select district",
            options:
              districts?.map((district: any) => ({
                value: district.id,
                label: district.name,
              })) || [],
          },
        },
        {
          field_name: "penalty_amount",
          label: "Penalty Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter penalty amount (e.g., 30000)",
            min: 0,
            step: "0.01",
          },
        },

        {
          field_name: "surcharge",
          label: "Surcharge Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter surcharge amount (e.g., 30000)",
            min: 0,
            step: "0.01",
          },
        },
        {
          field_name: "grace_period",
          label: "Grace Period",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter grace period (e.g., 6)",
            min: 0,
            step: "1",
          },
        },
      ],
    },
  ];

  const title = "Penalty Setting Management";
  const redirect = getRegister(URL_PENALTY_SETTINGS);

  return {
    sections,
    title,
    redirect,
    loading,
    data,
    loadingDistricts,
    districts,
  };
};

export const PenaltySettingForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = usePenaltySettingForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
